import React, { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { UploadIcon, X, InfoIcon } from "lucide-react";
import { filledFormService } from "@/features/filledForm/filledFormService";

interface UploadFile {
  id: string;
  fileName: string;
  fileSize: number;
  progress: number;
  status: "uploading" | "completed" | "error";
  path?: string;
  file?: File;
}

interface SimplifiedUploadFieldProps {
  value: any[];
  onChange: (value: any[]) => void;
  label: string;
  description?: string;
  required?: boolean;
  videoUrl?: string;
  multiline?: boolean;
  processId?: string;
  phaseFormId?: string;
  formFieldId?: string;
}

export const SimplifiedUploadField: React.FC<SimplifiedUploadFieldProps> = ({
  value = [],
  onChange,
  label,
  description,
  required = false,
  videoUrl,
  multiline = false,
  processId = "",
  phaseFormId = "",
  formFieldId
}) => {
  const [uploadFiles, setUploadFiles] = useState<UploadFile[]>(value);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // ID do campo do formulário - usa o fornecido ou extrai do label se não fornecido
  const fieldId = formFieldId || label?.toLowerCase().replace(/\s+/g, '_') || 'upload_field';

  // Sincronizar o estado local com o valor da prop
  useEffect(() => {
    if (value && Array.isArray(value) && value.length > 0) {
      setUploadFiles(value);
    }
  }, [value]);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    
    // Se não for multiline, limitar a apenas um arquivo
    if (!multiline && (files.length > 1 || uploadFiles.length > 0)) {
      // Permitir apenas substituir o arquivo existente
      if (uploadFiles.length > 0) {
        setUploadFiles([]);
      }
      // E usar apenas o primeiro arquivo selecionado
      const filesToProcess = [files[0]];
      processFiles(filesToProcess);
    } else {
      processFiles(Array.from(files));
    }
    
    // Limpar o input para permitir selecionar o mesmo arquivo novamente
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  
  const processFiles = async (files: File[]) => {
    // Criar objetos de upload para os arquivos
    const newUploadFiles = files.map(file => ({
      id: Math.random().toString(36).substring(2, 11),
      fileName: file.name,
      fileSize: file.size,
      progress: 0,
      status: "uploading" as const,
      file: file
    }));
    
    // Atualizar estado e notificar mudança para o componente pai
    const updatedUploadFiles = multiline 
      ? [...uploadFiles, ...newUploadFiles] 
      : newUploadFiles;
    
    setUploadFiles(updatedUploadFiles);
    onChange(updatedUploadFiles);
    
    // Iniciar upload de cada arquivo
    const uploadPromises = newUploadFiles.map(async (uploadFile) => {
      try {
        const result = await filledFormService.uploadDocument(
          phaseFormId,
          processId,
          fieldId,
          uploadFile.file as File,
          (progress) => {
            // Atualizar progresso durante upload
            setUploadFiles(prev => {
              const updated = prev.map(f =>
                f.id === uploadFile.id
                  ? { ...f, progress }
                  : f
              );
              onChange(updated);
              return updated;
            });
          }
        );
        
        // Atualizar status quando upload concluído
        setUploadFiles(prev => {
          const updated = prev.map(f =>
            f.id === uploadFile.id
              ? { 
                  ...f, 
                  status: "completed" as const, 
                  path: result.path, 
                  progress: 100
                }
              : f
          );
          onChange(updated);
          return updated;
        });
        
        return result.path;
      } catch (error) {
        // Atualizar status em caso de erro
        setUploadFiles(prev => {
          const updated = prev.map(f =>
            f.id === uploadFile.id
              ? { ...f, status: "error" as const }
              : f
          );
          onChange(updated);
          return updated;
        });
        
        console.error("Erro ao fazer upload do arquivo:", error);
        return null;
      }
    });
    
    await Promise.all(uploadPromises);
  };
  
  const handleRemoveFile = (id: string) => {
    const updatedFiles = uploadFiles.filter(file => file.id !== id);
    setUploadFiles(updatedFiles);
    onChange(updatedFiles);
  };

  return (
    <div className="w-full">
      <div className="flex items-center mb-2">
        <label className="text-slate-500 font-bold px-2">
          {label}{required && <span className="text-red-500">*</span>}
        </label>
        {description && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger><InfoIcon size={16} className="text-grey-light ml-1" /></TooltipTrigger>
              <TooltipContent>
                <p>{description}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
      
      <div className="space-y-2">
        <input
          type="file"
          multiple={multiline}
          onChange={handleFileSelect}
          className="hidden"
          ref={fileInputRef}
          id={`file-upload-${fieldId}`}
        />
        
        <Button
          type="button"
          variant="outline"
          className="w-full justify-center py-6"
          onClick={() => fileInputRef.current?.click()}
          disabled={!multiline && uploadFiles.length > 0}
        >
          <UploadIcon className="mr-2 h-5 w-5" />
          {multiline ? "Adicionar arquivos" : "Selecionar arquivo"}
        </Button>
        
        <div className="text-xs text-gray-500 ml-2">
          {multiline 
            ? "Você pode adicionar múltiplos arquivos" 
            : "Você pode adicionar apenas um arquivo"}
        </div>
        
        {/* Lista de arquivos */}
        {uploadFiles.length > 0 && (
          <div className="space-y-2 mt-4">
            {uploadFiles.map((file) => (
              <div 
                key={file.id} 
                className="flex items-center justify-between p-3 border rounded-md"
              >
                <div className="flex-1 overflow-hidden">
                  <div className="font-medium truncate">{file.fileName}</div>
                  <div className="text-xs text-gray-500">
                    {(file.fileSize / 1024).toFixed(1)} KB
                  </div>
                  
                  {/* Status do upload */}
                  <div className="mt-1">
                    {file.status === 'uploading' && (
                      <>
                        <div className="text-xs text-blue-500">
                          Enviando ({Math.round(file.progress)}%)
                        </div>
                        <div className="w-full h-1 bg-gray-200 rounded-full mt-1">
                          <div 
                            className="h-full bg-blue-500 rounded-full" 
                            style={{ width: `${file.progress}%` }}
                          />
                        </div>
                      </>
                    )}
                    
                    {file.status === 'completed' && (
                      <div className="text-xs text-green-500">
                        Enviado com sucesso
                      </div>
                    )}
                    
                    {file.status === 'error' && (
                      <div className="text-xs text-red-500">
                        Erro ao enviar
                      </div>
                    )}
                  </div>
                </div>
                
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleRemoveFile(file.id)}
                  className="ml-2"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}; 